import type { Dispatch, SetStateAction } from "react";
import pmImage from "../assets/pm.jpg";

type ShopProduct = {
  id: string;
  title: string;
  price: string;
  oldPrice: string;
  badge?: string;
  image: string;
};

type ShopTabProps = {
  shopSortOptions: string[];
  shopSort: string;
  setShopSort: Dispatch<SetStateAction<string>>;
  shopPageItems: ShopProduct[];
  shopProductEnTitles: Record<string, string>;
  shopPage: number;
  shopTotalPages: number;
  setShopPage: Dispatch<SetStateAction<number>>;
};

export default function ShopTab({
  shopSortOptions,
  shopSort,
  setShopSort,
  shopPageItems,
  shopProductEnTitles,
  shopPage,
  shopTotalPages,
  setShopPage
}: ShopTabProps) {
  return (
    <section className="mb-10 space-y-0 md:mb-16">
      <div className="relative h-[260px] overflow-hidden md:h-[340px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${pmImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,13,21,0.15)] via-[rgba(8,13,21,0.28)] to-[rgba(8,13,21,0.35)]" />
        <div className="relative z-10 flex h-full items-center justify-center px-6 text-center">
          <h2 className="text-3xl font-semibold text-white md:text-5xl">
            shop
          </h2>
        </div>
      </div>

      <div className="bg-white py-14">
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="flex justify-end">
            <label htmlFor="shop-sort" className="sr-only">
              Sort products
            </label>
            <select
              id="shop-sort"
              value={shopSort}
              onChange={(event) => {
                setShopSort(event.target.value);
                setShopPage(1);
              }}
              className="h-10 rounded-md border border-[#e5e7eb] bg-white px-4 text-sm text-[#6b7280] shadow-[0_8px_18px_rgba(15,23,36,0.06)] outline-none transition focus:border-[#0d5be1]"
            >
              {shopSortOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {shopPageItems.map((product) => (
              <article
                key={product.id}
                className="flex flex-col items-center gap-4 text-center"
              >
                <div className="relative w-full">
                  <div className="overflow-hidden rounded-2xl shadow-[0_16px_32px_rgba(15,23,36,0.16)]">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="h-[180px] w-full object-cover"
                    />
                  </div>
                  {product.badge && (
                    <span className="absolute right-3 top-3 rounded-full bg-[#1f2b3b] px-2.5 py-1 text-[10px] font-semibold text-white/90">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  <h3 className="max-w-[220px] text-sm font-semibold leading-snug text-[#111827]">
                    {product.title}
                  </h3>
                  <p className="max-w-[220px] text-[11px] text-[#9aa3af]">
                    {shopProductEnTitles[product.id]}
                  </p>
                  <p className="text-sm font-semibold text-[#111827]">
                    {product.price}
                    <span className="ml-1 text-[11px] font-normal text-[#9aa3af] line-through">
                      {product.oldPrice}
                    </span>
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 flex items-center justify-between gap-4 border-t border-[#eef2f6] pt-6 text-sm text-[#1f2b3b]">
            <button
              type="button"
              onClick={() => setShopPage((page) => Math.max(1, page - 1))}
              disabled={shopPage === 1}
              className="rounded-md border border-[#e5e7eb] px-3 py-1.5 text-sm text-[#1f2b3b] transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              上一页
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: shopTotalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isActive = pageNumber === shopPage;
                return (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setShopPage(pageNumber)}
                    aria-current={isActive ? "page" : undefined}
                    className={`flex h-7 w-7 items-center justify-center rounded text-[12px] font-semibold transition ${
                      isActive
                        ? "cursor-default bg-[#f3b455] text-[#1d1d1d]"
                        : "bg-[#0b1c2b] text-white hover:bg-[#122746]"
                    }`}
                    disabled={isActive}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() =>
                setShopPage((page) => Math.min(shopTotalPages, page + 1))
              }
              disabled={shopPage === shopTotalPages}
              className="rounded-md border border-[#e5e7eb] px-3 py-1.5 text-sm text-[#1f2b3b] transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
